import { z } from 'zod';
/**
 * RUT chileno: normalizacion, validacion del digito verificador y formato.
 *
 * ## Por que esto vive en el contrato y no en un helper de `apps/api`
 *
 * El RUT es **la identidad del cliente** en todos los sistemas del grupo: es lo
 * que el POS captura en el mostrador, lo que SAP guarda en el `CardCode`, y —en
 * F2— lo que decide si dos ventas son del mismo contacto de HubSpot o de dos
 * contactos distintos. Esa decision tiene que tomarse con la misma regla en los
 * dos extremos del cable, y por eso vive donde viven los contratos.
 *
 * ## El problema real que resuelve
 *
 * El mismo cliente escribe su RUT de cinco formas: `12.345.678-5`,
 * `12345678-5`, `123456785`, `12345678-k`, `12.345.678-K`. Todas son el mismo
 * documento y **como texto son cinco claves distintas**. Deduplicar por el texto
 * crudo crea cinco contactos en HubSpot para una sola persona, y el error no se
 * ve hasta que marketing manda el mismo correo cinco veces.
 *
 * La forma canonica que elige este modulo es **cuerpo sin puntos + guion +
 * digito verificador en mayuscula** (`12345678-5`), que es la que ya documenta
 * el contrato de DecoraPOS (`decoraposCustomerSchema.document`).
 *
 * ## Lo que este modulo NO hace
 *
 * No se enchufa al schema de DecoraPOS. Ese contrato acepta `document` como
 * texto libre a proposito —el POS tambien registra pasaportes— y estrecharlo
 * ahora seria un cambio incompatible que rechazaria ventas legitimas en el
 * mostrador. Aca la regla es otra: quien necesita el RUT como CLAVE lo normaliza
 * y, si no lo es, sigue adelante con la otra identidad que tenga (el correo).
 * Un RUT invalido no puede costar una venta.
 */
/**
 * Largo del cuerpo del RUT.
 *
 * **Minimo 6**: por debajo estan los RUT de personas nacidas antes de ~1900
 * —irrelevantes en un padron activo— y, sobre todo, el comodin `1-9` que varios
 * ERP chilenos usan como cliente generico. Rechazarlo juega a favor: el contacto
 * cae al correo en vez de fundirse con otros.
 *
 * **Maximo 9**: las personas no pasan hoy de 8 digitos y las empresas van en el
 * rango 70-99 millones, asi que 9 es holgado sin dejar pasar basura larga.
 */
const RUT_BODY_MIN_DIGITS = 6;
const RUT_BODY_MAX_DIGITS = 9;
/**
 * Digito verificador de un cuerpo de RUT, por modulo 11.
 *
 * El cuerpo se recorre de derecha a izquierda multiplicando por la serie
 * 2,3,4,5,6,7 que vuelve a empezar. El resto 11 es `0` y el resto 10 es `K`:
 * son los dos casos que un validador escrito de memoria suele omitir, y por eso
 * los dos tienen test propio.
 */
export function rutCheckDigit(body) {
    let sum = 0;
    let multiplier = 2;
    for (let index = body.length - 1; index >= 0; index -= 1) {
        sum += Number(body[index]) * multiplier;
        multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    const remainder = 11 - (sum % 11);
    if (remainder === 11) {
        return '0';
    }
    if (remainder === 10) {
        return 'K';
    }
    return String(remainder);
}
/**
 * RUT en su forma canonica (`12345678-5`), o `null` si el texto no es un RUT.
 *
 * Devuelve `null` en vez de lanzar porque **el llamador habitual no puede
 * fallar**: un pedido con un documento que no es RUT (un pasaporte, un cliente
 * extranjero, un campo mal tipeado) sigue siendo un pedido valido. Quien
 * necesite el rechazo duro usa `rutSchema`.
 */
export function normalizeRut(value) {
    if (value === null || value === undefined) {
        return null;
    }
    // Se quitan espacios, puntos y guiones de cualquier posicion: llegan
    // repartidos de formas que no vale la pena enumerar.
    const limpio = value.replace(/[\s.-]/g, '').toUpperCase();
    if (limpio.length < 2) {
        return null;
    }
    const cuerpoCrudo = limpio.slice(0, -1);
    const digito = limpio.slice(-1);
    if (!/^\d+$/.test(cuerpoCrudo)) {
        return null;
    }
    if (!/^[\dK]$/.test(digito)) {
        return null;
    }
    // Los ceros a la izquierda no participan del modulo 11, asi que `05126663` y
    // `5126663` son el MISMO documento. Si no se quitaran, serian dos claves
    // distintas para el mismo cliente — justo lo que este modulo existe para
    // evitar.
    const cuerpo = cuerpoCrudo.replace(/^0+/, '');
    if (cuerpo.length < RUT_BODY_MIN_DIGITS || cuerpo.length > RUT_BODY_MAX_DIGITS) {
        return null;
    }
    if (rutCheckDigit(cuerpo) !== digito) {
        return null;
    }
    return `${cuerpo}-${digito}`;
}
/** `true` si el texto es un RUT con digito verificador correcto. */
export function isValidRut(value) {
    return normalizeRut(value) !== null;
}
/**
 * RUT aritmeticamente valido que **NO identifica a una persona**.
 *
 * ## Por que esto existe, con el desastre concreto que evita
 *
 * `66666666-6` y `11111111-1` pasan el modulo 11 y son los RUT que media
 * industria chilena usa para la boleta anonima —"consumidor final"—. Como F2 usa
 * el RUT de clave unica en HubSpot, aceptarlos **funde todas las ventas de
 * mostrador en UN SOLO contacto**, con el historial de compras de cientos de
 * personas distintas y el correo de la ultima. Y como la propiedad es unica, eso
 * no se deshace: hay que separar los contactos a mano.
 *
 * La regla —todos los digitos iguales— cubre los que se usan de verdad sin
 * mantener una lista que siempre va a estar incompleta. Un RUT real con los
 * ocho digitos iguales existe en teoria y es un precio ridiculo comparado con lo
 * de arriba.
 *
 * `1-9` cae por el largo minimo del cuerpo, que es la otra mitad de esta
 * defensa: es el otro comodin habitual en los ERP chilenos.
 */
export function isPlaceholderRut(value) {
    const canonico = normalizeRut(value);
    if (canonico === null) {
        return false;
    }
    const cuerpo = canonico.split('-')[0];
    return new Set(cuerpo).size === 1;
}
/**
 * RUT canonico **utilizable como clave de identidad**, o `null`.
 *
 * Es `normalizeRut` mas el filtro de comodines. Lo que necesite el RUT para
 * DEDUPLICAR usa este; lo que solo necesite normalizarlo para mostrarlo o
 * mandarlo como dato usa `normalizeRut`.
 */
export function identifyingRut(value) {
    const canonico = normalizeRut(value);
    return canonico === null || isPlaceholderRut(canonico) ? null : canonico;
}
/**
 * RUT con puntos, para mostrarlo (`12.345.678-5`).
 *
 * Es solo presentacion: **nunca** se usa como clave. Si el texto no es un RUT
 * valido se devuelve tal cual, porque formatear no es validar y una pantalla no
 * es el lugar donde se descubre que un dato esta mal.
 */
export function formatRut(value) {
    const canonico = normalizeRut(value);
    if (canonico === null) {
        return value;
    }
    const [cuerpo, digito] = canonico.split('-');
    const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${conPuntos}-${digito}`;
}
/**
 * RUT obligatorio y valido, ya normalizado a la salida del parse.
 *
 * Para los bordes donde el RUT SI es obligatorio (una nota de credito, un
 * documento tributario). El borde del POS no lo usa: ver el docblock del modulo.
 */
export const rutSchema = z
    .string()
    .trim()
    .transform((value, ctx) => {
    const canonico = normalizeRut(value);
    if (canonico === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `"${value}" no es un RUT valido: revisa el cuerpo y el digito verificador.`,
        });
        return z.NEVER;
    }
    return canonico;
});
//# sourceMappingURL=rut.js.map